import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Accordion, Link, Section, Text, Quote } from '@/components';
import './FAQSection.scss'

interface FaqItem {
  id?: string
  title: string
  content: any
}


const FAQ_DATA: FaqItem[] = [
  {
    title: "1. How is IntelliRoom™ better than ChatGPT?",
    content: (
      <Text.Description size="xs" className="text-left text-[#E3E3E3]">
        IntelliRoom™ is purpose-built for institutional investors, not chat. It reads data rooms, parses Excel models,
        flags risks and opportunities with red/green indicators, and cites its sources in a secure, investor-ready
        workspace. Unlike general AI tools, it supports real diligence workflows and creates a shared source of truth for
        your team.
      </Text.Description>
    )
  },
  {
    title: "2. How is my data handled?",
    content: (
      <div className="space-y-4 text-left text-[#E3E3E3]">
        <Text.Description size="xs" className="text-left text-[#E3E3E3]">
          We do not use your data to train AI, nor do we share or sell it to third parties. Your data is used solely to
          provide the services you request.
        </Text.Description>
        <Text.Description size="xs" className="text-left text-[#E3E3E3]">
          We collect limited, aggregated, and anonymized usage analytics to improve platform performance and user
          experience while maintaining enterprise-grade privacy standards.
        </Text.Description>
        <Text.Description size="xs" className="text-left text-[#E3E3E3]">
          IntelliRoom collects high-level usage data (such as feature usage and session frequency) to enhance reliability
          and performance—never the contents of your private data rooms.
        </Text.Description>
        <Text.Description size="xs" className="text-left text-[#E3E3E3]">
          All data is encrypted at rest and in transit, and access is restricted to authorized users only.
        </Text.Description>
        <Text.Description size="xs" className="text-left text-[#E3E3E3]">
          See our{" "}
          <Link
            to="/legal/data-use-policy"
            className="!p-0 text-secondary underline"
            disableDefaultConfig
          >
            <Quote className="!text-[12px] !leading-[20px] md:!text-[16px] md:!leading-[28px]">Data Use Policy</Quote>
          </Link>{" "}
          and{" "}
          <Link
            to="/legal/privacy-policy"
            className="!p-0 text-secondary underline"
            disableDefaultConfig
          >
            <Quote className="!text-[12px] !leading-[20px] md:!text-[16px] md:!leading-[28px]">Privacy Policy</Quote>
          </Link>{" "}
          for full details.
        </Text.Description>
      </div>
    )
  },
  {
    title: "3. Is there a storage limit?",
    content: (
      <div className="space-y-4 text-left text-[#E3E3E3]">
        <Text.Description size="xs" className="text-left text-[#E3E3E3]">
          To obtain detailed pricing and system limits information please{" "}
          <Link
            to="https://calendly.com/dustinrbyington/30min"
            target="_blank"
            rel="noreferrer"
            className="!p-0 text-secondary underline"
            disableDefaultConfig
          >
            reach out to our sales team
          </Link>
          .
        </Text.Description>
        <Text.Description size="xs" className="text-left text-[#E3E3E3]">
          The limits depend on the pricing tier. Technological limits exist, but are far above reasonable
          diligence-related data room sizes.
        </Text.Description>
      </div>
    )
  },
  {
    title: "4. How does the free trial work?",
    content: (
      <div className="space-y-4 text-left text-[#E3E3E3]">
        <Text.Description size="xs" className="text-left text-[#E3E3E3]">
          Your 14-day free trial gives you full platform access with:
        </Text.Description>
        <ul className="list-disc list-inside space-y-2 text-left text-[#E3E3E3] text-[12px] md:text-[16px]">
          <li>Up to 75 deals</li>
          <li>7 seats</li>
          <li>Support for XLSX, PDF, DOCX, PPTX, and more</li>
          <li>No credit card required</li>
        </ul>
        <Text.Description size="xs" className="text-left text-[#E3E3E3]">
          You can export your data anytime—no lock-in.
        </Text.Description>
        <Text.Description size="xs" className="text-left text-[#E3E3E3]">
          After your trial:
        </Text.Description>
        <ul className="list-disc list-inside space-y-2 text-left text-[#E3E3E3] text-[12px] md:text-[16px]">
          <li>Data remains encrypted and accessible for 90 days.</li>
          <li>You can view or delete all uploaded files during that period.</li>
          <li>If no subscription is purchased after 90 days, your account and data are permanently deleted.</li>
        </ul>
      </div>
    )
  },
  {
    title: "5. What counts as a deal?",
    content: (
      <Text.Description size="xs" className="text-left text-[#E3E3E3]">
        Each company or data room you upload counts as one deal, regardless of how many documents it contains. Whether
        it’s a single PDF or a full diligence package, it’s counted as one deal.
      </Text.Description>
    )
  },
  {
    title: "6. What are the roles and who sees what?",
    content: (
      <div className="space-y-4 text-left text-[#E3E3E3]">
        <div className="overflow-x-auto">
          <table className="min-w-[320px] w-full border border-white/10 text-left text-[#E3E3E3] text-[12px] md:text-[14px]">
            <thead>
              <tr>
                <th className="px-3 py-2 border border-white/10 font-medium text-[#F6F6F6]">Permission / Role</th>
                <th className="px-3 py-2 border border-white/10 font-medium text-[#F6F6F6]">Owner</th>
                <th className="px-3 py-2 border border-white/10 font-medium text-[#F6F6F6]">Admin</th>
                <th className="px-3 py-2 border border-white/10 font-medium text-[#F6F6F6]">User</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="px-3 py-2 border border-white/10">Manage subscription, billing, and account deletion</td>
                <td className="px-3 py-2 border border-white/10">Yes</td>
                <td className="px-3 py-2 border border-white/10">No</td>
                <td className="px-3 py-2 border border-white/10">No</td>
              </tr>
              <tr>
                <td className="px-3 py-2 border border-white/10">Invite and manage team members</td>
                <td className="px-3 py-2 border border-white/10">Yes</td>
                <td className="px-3 py-2 border border-white/10">Yes</td>
                <td className="px-3 py-2 border border-white/10">No</td>
              </tr>
              <tr>
                <td className="px-3 py-2 border border-white/10">Create or delete IntelliRooms</td>
                <td className="px-3 py-2 border border-white/10">Yes</td>
                <td className="px-3 py-2 border border-white/10">Yes</td>
                <td className="px-3 py-2 border border-white/10">No</td>
              </tr>
              <tr>
                <td className="px-3 py-2 border border-white/10">Ask AI questions</td>
                <td className="px-3 py-2 border border-white/10">Yes</td>
                <td className="px-3 py-2 border border-white/10">Yes</td>
                <td className="px-3 py-2 border border-white/10">Yes</td>
              </tr>
              <tr>
                <td className="px-3 py-2 border border-white/10">View all data, diligence flags, and answers</td>
                <td className="px-3 py-2 border border-white/10">Yes</td>
                <td className="px-3 py-2 border border-white/10">Yes</td>
                <td className="px-3 py-2 border border-white/10">Yes</td>
              </tr>
            </tbody>
          </table>
        </div>
        <Text.Description size="xs" className="text-left text-[#E3E3E3]">
          All members of a workspace share access to the same data sets, ensuring a single source of truth.
        </Text.Description>
      </div>
    )
  },
  {
    id: 'faq-excel',
    title: "7. How does it work with Excel?",
    content: (
      <div className="space-y-4 text-left text-[#E3E3E3]">
        <Text.Description size="xs" className="text-left text-[#E3E3E3]">
          IntelliRoom™ AI is optimized for financial models and complex spreadsheets, improving accuracy and reducing
          hallucinations.
        </Text.Description>
        <ul className="list-disc list-inside space-y-2 text-left text-[#E3E3E3] text-[12px] md:text-[16px]">
          <li>
            <strong>Structured data (CSV or clean tables):</strong> Processed as tabular data for maximum accuracy.
          </li>
          <li>
            <strong>Complex spreadsheets (formulas, merged cells, irregular layouts):</strong> IntelliRoom™ agents provide
            approximate locations where the required information could be found instead of fabricating uncertain results.
          </li>
        </ul>
        <Text.Description size="xs" className="text-left text-[#E3E3E3]">
          <strong>Best results:</strong> Clean tabular data.
          <br />
          <strong>Limitations:</strong> Lower accuracy with heavily nested formulas or irregular formatting.
        </Text.Description>
        <Text.Description size="xs" className="text-left text-[#E3E3E3] font-semibold">
          Tips to improve accuracy and reduce cost:
        </Text.Description>
        <ul className="list-disc list-inside space-y-2 text-left text-[#E3E3E3] text-[12px] md:text-[16px]">
          <li>Keep data in clear rows and columns.</li>
          <li>Add context such as metric definitions or relationships between sheets.</li>
        </ul>
      </div>
    )
  },
  {
    title: "8. Where does the AI get its answers from?",
    content: (
      <Text.Description size="xs" className="text-left text-[#E3E3E3]">
        Answers are generated from your uploaded documents as well as relevant, verified web sources. Every output
        includes citations, and uncertain sections are flagged transparently.
      </Text.Description>
    )
  },
  {
    title: "9. What AI does IntelliRoom™ use?",
    content: (
      <div className="space-y-4 text-left text-[#E3E3E3]">
        <Text.Description size="xs" className="text-left text-[#E3E3E3]">
          IntelliRoom™ runs a set of proprietary AI agents powered by various AI models and data retrieval systems that
          collaborate to provide accurate answers and actionable insights while maintaining data security and efficiency.
        </Text.Description>
        <Text.Description size="xs" className="text-left text-[#E3E3E3]">
          Our proprietary enterprise-grade agent management platform enables:
        </Text.Description>
        <ul className="list-disc list-inside space-y-2 text-left text-[#E3E3E3] text-[12px] md:text-[16px]">
          <li>Local or private cloud hosting (data never leaves your environment)</li>
          <li>Integration of multiple best-in-class models</li>
          <li>Vendor-agnostic scalability and performance</li>
        </ul>
        <Text.Description size="xs" className="text-left text-[#E3E3E3]">
          Our public cloud deployment is hosted on AWS and uses private deployments of Anthropic Claude models, with
          additional models for embeddings, transcription, and specialized tasks.
        </Text.Description>
      </div>
    )
  },
];
const FAQ_TARGET_ID = 'faq-excel'
const FAQ_TARGET_INDEX = FAQ_DATA.findIndex((item) => item.id === FAQ_TARGET_ID)
const INITIAL_SCROLL_DELAY = 1000
const SCROLL_RETRY_INTERVAL = 150
const SCROLL_RETRY_TIMEOUT = 5000

const getNow = () => {
  if (typeof window !== 'undefined' && window.performance && typeof window.performance.now === 'function') {
    return window.performance.now()
  }

  return Date.now()
}

export const FAQSection = () => {
  const [activeTab, setActiveTab] = useState<number | null>(null)
  const location = useLocation()
  const shouldScrollToTarget = useRef(false)
  const scrollAttemptTimeoutId = useRef<number | null>(null)
  const scrollStartTimestamp = useRef<number | null>(null)
  const initialScrollDelayTimeoutId = useRef<number | null>(null)

  const cancelScheduledScroll = useCallback(() => {
    if (scrollAttemptTimeoutId.current !== null) {
      window.clearTimeout(scrollAttemptTimeoutId.current)
      scrollAttemptTimeoutId.current = null
    }
  }, [])

  const cancelInitialScrollDelay = useCallback(() => {
    if (initialScrollDelayTimeoutId.current !== null) {
      window.clearTimeout(initialScrollDelayTimeoutId.current)
      initialScrollDelayTimeoutId.current = null
    }
  }, [])

  const scheduleScrollAttempt = useCallback(() => {
    if (!shouldScrollToTarget.current) {
      cancelScheduledScroll()
      return
    }

    const now = getNow()
    if (scrollStartTimestamp.current === null) {
      scrollStartTimestamp.current = now
    }

    const targetElement = document.getElementById(FAQ_TARGET_ID)

    if (!targetElement) {
      cancelScheduledScroll()
      scrollAttemptTimeoutId.current = window.setTimeout(scheduleScrollAttempt, SCROLL_RETRY_INTERVAL)
      return
    }

    const contentElement = targetElement.querySelector<HTMLElement>('.accordion-content')
    const isExpanded = targetElement.classList.contains('active') || (contentElement ? contentElement.clientHeight > 0 : false)

    if (!isExpanded) {
      const hasTimedOut = now - (scrollStartTimestamp.current ?? now) >= SCROLL_RETRY_TIMEOUT

      if (hasTimedOut) {
        shouldScrollToTarget.current = false
        cancelScheduledScroll()
        return
      }

      cancelScheduledScroll()
      scrollAttemptTimeoutId.current = window.setTimeout(scheduleScrollAttempt, SCROLL_RETRY_INTERVAL)
      return
    }

    window.requestAnimationFrame(() => {
      const scrollTarget = targetElement.querySelector<HTMLElement>('.accordion-header-inner') ?? targetElement
      scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'center' })
      shouldScrollToTarget.current = false
      scrollStartTimestamp.current = null
      cancelScheduledScroll()
    })
  }, [cancelScheduledScroll])

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const hasQueryMatch = searchParams.get('action') === FAQ_TARGET_ID
    const hasHashMatch = location.hash === `#${FAQ_TARGET_ID}`

    if ((!hasHashMatch && !hasQueryMatch) || FAQ_TARGET_INDEX === -1) {
      shouldScrollToTarget.current = false
      scrollStartTimestamp.current = null
      cancelScheduledScroll()
      cancelInitialScrollDelay()
      return
    }

    setActiveTab(FAQ_TARGET_INDEX)
    shouldScrollToTarget.current = true
    scrollStartTimestamp.current = null
    cancelScheduledScroll()
    cancelInitialScrollDelay()

    window.requestAnimationFrame(() => {
      initialScrollDelayTimeoutId.current = window.setTimeout(() => {
        scheduleScrollAttempt()
      }, INITIAL_SCROLL_DELAY)
    })
  }, [location.hash, location.search, cancelInitialScrollDelay, cancelScheduledScroll, scheduleScrollAttempt])

  useEffect(() => () => {
    shouldScrollToTarget.current = false
    scrollStartTimestamp.current = null
    cancelScheduledScroll()
    cancelInitialScrollDelay()
  }, [cancelInitialScrollDelay, cancelScheduledScroll])

  return (
    <Section id="faq-section" className="!py-0 faq-section bg-black">
      <div className="w-full max-w-[1108px] mx-auto text-center">
        <div className="hidden md:flex justify-center gap-2 mb-8 mx-auto">
          <Text.Heading className="text-[36px] md:text-[52px] font-400 font-calSans text-white">Frequently</Text.Heading>
          <Text.Heading className="text-[36px] md:text-[52px] font-400 font-calSans faq-title">Asked</Text.Heading>
          <Text.Heading className="text-[36px] md:text-[52px] font-400 font-calSans faq-title2">Questions</Text.Heading>
        </div>

        <div className="flex flex-col md:hidden justify-center gap-2 mb-8 mx-auto">
          <Text.Heading className="text-[36px] text-white leading-[38px] font-400 font-calSans faq-title">Frequently Asked</Text.Heading>
          <Text.Heading className="text-[36px] text-white leading-[38px] font-400 font-calSans faq-title2">Questions</Text.Heading>
        </div>

        <div className="w-full">
          <Accordion items={FAQ_DATA} defaultActiveTab={activeTab ?? undefined} />
        </div>
      </div>
    </Section>
  )
};
